import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Button } from '@dgm/ui';
import { Play as PlayIcon, Gamepad2 } from 'lucide-react';
import { playApi } from '../lib/api.js';
import { AdSlot } from '../components/ad-slot.js';

/**
 * PUBLIC published-game viewer (cycle8-2b, D-138) — the brief's "public join":
 * unauthenticated viewers open /play/{slug}, see an ad (placeholder provider,
 * D-038) while a countdown runs, then play the game in a sandboxed iframe.
 * No login required; a top-level public route (sibling of /login).
 *
 * HOW TO DISABLE: FEATURES.publicPublish=false makes the API 404 every slug —
 * this page then renders its not-found state. (The page itself needs no flag.)
 */
export function PlayPage() {
  const { slug = '' } = useParams();
  const [meta, setMeta] = useState<{ title: string; intentSummary?: string; requiresAd: boolean } | null>(null);
  const [notFound, setNotFound] = useState(false);
  const [adCleared, setAdCleared] = useState(false);
  const [gameUrl, setGameUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    let live = true;
    playApi
      .resolve<{ title: string; intentSummary?: string; requiresAd: boolean }>(slug)
      .then((res) => live && setMeta(res))
      .catch(() => live && setNotFound(true));
    return () => {
      live = false;
    };
  }, [slug]);

  async function start() {
    setLoading(true);
    try {
      const { url } = await playApi.url<{ url: string }>(slug);
      setGameUrl(url);
    } catch {
      setNotFound(true);
    } finally {
      setLoading(false);
    }
  }

  if (notFound) {
    return (
      <Shell>
        <Gamepad2 className="h-10 w-10 text-muted-foreground" />
        <h1 className="text-xl font-semibold">This game isn’t here anymore</h1>
        <p className="text-sm text-muted-foreground">
          The link may have been unpublished by its creator.
        </p>
      </Shell>
    );
  }
  if (!meta) {
    return (
      <Shell>
        <p className="text-muted-foreground">Loading…</p>
      </Shell>
    );
  }
  if (gameUrl) {
    return (
      <div className="fixed inset-0 bg-black">
        <iframe
          title={meta.title}
          src={gameUrl}
          sandbox="allow-scripts"
          className="h-full w-full border-0"
        />
      </div>
    );
  }
  return (
    <Shell>
      <h1 className="text-2xl font-bold">{meta.title}</h1>
      {meta.intentSummary ? (
        <p className="max-w-md text-center text-sm text-muted-foreground">{meta.intentSummary}</p>
      ) : null}
      {meta.requiresAd && !adCleared ? (
        <div className="w-full max-w-md">
          <AdSlot onCleared={() => setAdCleared(true)} />
        </div>
      ) : null}
      <Button size="lg" disabled={(meta.requiresAd && !adCleared) || loading} onClick={() => void start()}>
        <PlayIcon /> {loading ? 'Loading…' : 'Play now'}
      </Button>
      <p className="text-xs text-muted-foreground">
        Made with du-game-maker — a game by one of our creators.
      </p>
    </Shell>
  );
}

function Shell({ children }: { children: React.ReactNode }) {
  return (
    <div className="flex min-h-screen flex-col items-center justify-center gap-4 bg-background p-6">
      {children}
    </div>
  );
}
